/**
 * PhoneListField — mehrere Telefonnummern pro Kontakt mit Label + Primary-Marker.
 *
 * Datenmodell (analog EmailListField):
 *  - Legacy: string[] (nur Nummern, keine Labels)
 *  - Neu:    { number, label, is_primary }[]
 *
 * Backend `contact_utils.py` normalisiert die Mischform ebenfalls.
 */
import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash, Star } from "@phosphor-icons/react";

function toObj(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((it, idx) => {
      if (typeof it === "string") {
        return { number: it, label: "", is_primary: idx === 0 };
      }
      return {
        number: it?.number || it?.phone || "",
        label: it?.label || "",
        is_primary: !!it?.is_primary,
      };
    })
    .filter((e, idx, arr) => e.number || idx === arr.length - 1);
}

const COMMON_LABELS = ["mobil", "geschäftlich", "privat", "Zentrale", "Fax", "sonstige"];

export default function PhoneListField({ phones, onChange }) {
  const [items, setItems] = useState(() => toObj(phones).length ? toObj(phones) : [{ number: "", label: "", is_primary: true }]);

  useEffect(() => {
    const norm = toObj(phones);
    if (norm.length === 0) {
      setItems([{ number: "", label: "", is_primary: true }]);
    } else {
      if (!norm.some((p) => p.is_primary)) norm[0].is_primary = true;
      setItems(norm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = (list) => {
    setItems(list);
    const clean = list.filter((p) => (p.number || "").trim());
    if (clean.length && !clean.some((p) => p.is_primary)) clean[0].is_primary = true;
    onChange(clean);
  };

  const setItem = (idx, patch) => emit(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx) => {
    let next = items.filter((_, i) => i !== idx);
    if (next.length === 0) next = [{ number: "", label: "", is_primary: true }];
    if (!next.some((p) => p.is_primary)) next[0].is_primary = true;
    emit(next);
  };
  const makePrimary = (idx) => emit(items.map((it, i) => ({ ...it, is_primary: i === idx })));
  const addRow = () => emit([...items, { number: "", label: "", is_primary: false }]);

  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
        Telefonnummern
      </div>
      <div className="space-y-1.5" data-testid="contact-phones-list">
        {items.map((it, idx) => (
          <div key={idx} className="grid grid-cols-[1fr_140px_auto_auto] gap-1.5 items-center">
            <Input
              type="tel"
              placeholder="+49 …"
              value={it.number}
              onChange={(e) => setItem(idx, { number: e.target.value })}
              data-testid={`contact-phone-${idx}`}
              className="h-9"
            />
            <Input
              placeholder="Label (opt.)"
              value={it.label}
              onChange={(e) => setItem(idx, { label: e.target.value })}
              list={`phone-label-list-${idx}`}
              data-testid={`contact-phone-label-${idx}`}
              className="h-9 text-xs"
            />
            <datalist id={`phone-label-list-${idx}`}>
              {COMMON_LABELS.map((l) => <option key={l} value={l} />)}
            </datalist>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => makePrimary(idx)}
              disabled={!it.number}
              title={it.is_primary ? "Primäre Nummer" : "Als primär markieren"}
              data-testid={`contact-phone-primary-${idx}`}
              className="h-9 w-9"
            >
              {it.is_primary
                ? <Star weight="fill" className="text-amber-500" size={16} />
                : <Star className="text-slate-400" size={16} />}
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => removeItem(idx)}
              disabled={items.length === 1 && !it.number}
              title="Entfernen"
              data-testid={`contact-phone-remove-${idx}`}
              className="h-9 w-9 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
            >
              <Trash size={14} />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addRow}
          className="h-7 text-xs gap-1"
          data-testid="contact-phone-add"
        >
          <Plus size={12} /> Weitere Telefonnummer
        </Button>
      </div>
    </div>
  );
}
